import type { CartLine, ComponentizableCartLine } from "@shopify/hydrogen-react/storefront-api-types";
import React, { useMemo } from "react";

import { extractId } from "@repo/shopify/utils/common";
import { getDiscountName } from "@repo/shopify/utils/order";
import {
  checkProductLimitReached,
  checkProductLimitReachedCaa,
  checkVariantLimitReached,
  checkVariantLimitReachedCaa,
} from "@repo/shopify/utils/product";

import Button from "@atoms/Button";
import Image from "@atoms/Image";
import Link from "@atoms/Link";
import Price from "@atoms/Price";

import Stepper from "@molecules/Stepper";

import { NOTIFICATIONS } from "@const/notifications";
import { URLS } from "@const/urls";

import type useCartQuery from "@hooks/useCartQuery";
import useLanguage from "@hooks/useLanguage";

import { useCartStore } from "@store/cart";
import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

interface IProps {
  node: CartLine | ComponentizableCartLine;
  updateCartLine: ReturnType<typeof useCartQuery>["updateCartLine"];
  removeCartLine: ReturnType<typeof useCartQuery>["removeCartLine"];
  updateCartLineLoading: boolean;
  removeCartLineLoading: boolean;
}

export default function CartLineItem({ node, updateCartLine, removeCartLine, updateCartLineLoading, removeCartLineLoading }: IProps) {
  const { cart } = useCartStore();
  const { customer, caaCustomer } = useCustomerStore();
  const { openToast } = useLayoutStore();
  const { lang } = useLanguage();

  const color = useMemo(() => node.merchandise.selectedOptions.find((option) => option.name === "Color")?.value || "", [node.merchandise]);

  const size = useMemo(() => node.merchandise.selectedOptions.find((option) => option.name === "Size")?.value || "", [node.merchandise]);

  const handleRemove = async () => {
    if (updateCartLineLoading || removeCartLineLoading) return;
    await removeCartLine({ variables: { cartId: cart.id, lineIds: [node.id] } });
  };

  const handleChangeQuantity = (targetQuantity: number) => {
    if (updateCartLineLoading || removeCartLineLoading) return;

    const isProductLimitReached =
      lang === "jp"
        ? checkProductLimitReachedCaa({ cart, customer: caaCustomer, product: node.merchandise.product })
        : checkProductLimitReached({ cart, customer, product: node.merchandise.product });

    const isVariantLimitReached =
      lang === "jp"
        ? checkVariantLimitReachedCaa({
            cart,
            customer: caaCustomer,
            product: node.merchandise.product,
            selectedOption: node.merchandise.id,
          })
        : checkVariantLimitReached({
            cart,
            customer,
            product: node.merchandise.product,
            selectedOption: node.merchandise.id,
          });

    if (targetQuantity > (node.merchandise?.quantityAvailable || 99)) {
      openToast(NOTIFICATIONS.ERROR_EXCEED_QTY);
      return;
    }

    if (node.quantity <= targetQuantity && (isProductLimitReached || isVariantLimitReached)) {
      openToast(NOTIFICATIONS.ERROR_EXCEED_QTY);
      return;
    }

    void updateCartLine({ variables: { cartId: cart.id, lines: [{ id: node.id, quantity: targetQuantity }] } });
  };

  const isGift = node.merchandise.product.tags.includes("Gift");

  return (
    <div className="grid grid-cols-4 gap-2.5 border-b pb-5">
      <Link className="col-span-1" href={`${URLS.PRODUCTS}/${node.merchandise.product.handle}?variantId=${extractId(node.merchandise.id)}`}>
        <Image
          height={node.merchandise.image?.height || 1707}
          src={node.merchandise.image?.url || ""}
          width={node.merchandise.image?.width || 1280}
        />
      </Link>

      <div className="col-span-2 flex flex-col justify-between gap-2.5">
        <div>
          <span>{node.merchandise.product.title}</span>
          <span className="ml-1 font-normal">{`${color}, ${size}`}</span>
        </div>
        {!isGift && (
          <div className="flex gap-2.5">
            <Stepper
              handleChange={handleChangeQuantity}
              quantity={node.quantity}
              quantityAvailable={node.merchandise?.quantityAvailable || 99}
            />
          </div>
        )}
      </div>

      <div className="flex flex-col justify-between">
        <div className="col-span-1 flex flex-col items-end">
          <Price
            className="items-end"
            compareAtPrice={Number(node.cost.compareAtAmountPerQuantity?.amount || "0") * node.quantity}
            direction="vertical"
            price={Number(node.cost.totalAmount.amount || "0")}
            variant="secondary"
          />

          {node.discountAllocations.map((discount) => (
            <span className="text-xs font-normal whitespace-nowrap capitalize" key={getDiscountName(discount)}>
              {getDiscountName(discount)}
            </span>
          ))}
        </div>
        {!isGift && (
          <Button as="square" className="items-center" onClick={handleRemove} variant="tertiary">
            Remove
          </Button>
        )}
      </div>
    </div>
  );
}
